/**
 * Dictate into a web note field.
 *
 * Online (the web run is always online): prefer the browser SpeechRecognition
 * API so words stream into the field the same way native online dictation
 * does. If that API is missing or fails to start (common on iOS Safari),
 * fall back to MediaRecorder + the Gemini verbatim callable.
 *
 * Gesture matches native useDictation: tap latches, hold-to-talk stops
 * on release. Assembly matches native too: one committed line plus the
 * in-flight segment. Chrome ends after a pause; we restart the engine
 * without starting a new clip, so thinking just keeps the same line going.
 */

import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';
import { transcribeAudioFromWeb } from '../services/runApi';
import { appendTranscript, canRecordAudio, pickRecorderMime } from '../appendTranscript';

export const TAP_THRESHOLD_MS = 400;
export const DEFAULT_MAX_SECONDS = 60;
export const COUNTDOWN_VISIBLE_SECONDS = 10;

export const DICTATION_UNAVAILABLE = {
  NO_MODULE: 'noModule',
  NO_PERMISSION: 'noPermission',
  FAILED: 'failed',
} as const;

export type DictationUnavailable = typeof DICTATION_UNAVAILABLE[keyof typeof DICTATION_UNAVAILABLE];
export type DictationMode = 'live' | 'record';

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0?: { transcript?: string };
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: Event & { error?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

function speechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === 'undefined') return null;
  const w = window as Window & {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function canLiveDictate(): boolean {
  return !!speechRecognitionCtor();
}

function joinWithSpace(left: string, right: string): string {
  if (!left) return right;
  if (!right) return left;
  return `${left.replace(/\s+$/, '')} ${right.replace(/^\s+/, '')}`;
}

/** Silence long enough to read as the end of a sentence. */
export const SENTENCE_PAUSE_MS = 1200;

const SENTENCE_END = /[.!?…]["')\]]?$/;
const TIDY = /\s+/g;
/** Latin + accents, so PT-BR compares as well as EN. */
const COMPARE_STRIP = /[^a-z0-9\u00c0-\u024f]+/gi;

function tidy(text: string): string {
  return (text || '').replace(TIDY, ' ').trim();
}

function sameWords(left: string, right: string): string[] {
  return [
    left.toLowerCase().replace(COMPARE_STRIP, ' ').trim(),
    right.toLowerCase().replace(COMPARE_STRIP, ' ').trim(),
  ];
}

function capitalizeFirst(text: string): string {
  if (!text) return text;
  return text.charAt(0).toLocaleUpperCase() + text.slice(1);
}

function dropLeadingWords(text: string, count: number): string {
  if (count <= 0) return text;
  return text.split(' ').slice(count).join(' ').trim();
}

/**
 * Merge two fragments of one utterance: drop a repeat, let a growing
 * hypothesis replace the shorter one, otherwise append.
 *
 * Engines differ on whether a revised hypothesis updates its result slot or
 * lands in a new one, so a plain concatenation turns "I am here" into
 * "I I am I am here".
 */
export function mergeFragment(left: string, right: string): string {
  const start = tidy(left);
  const next = tidy(right);
  if (!start) return next;
  if (!next) return start;

  const [flatStart, flatNext] = sameWords(start, next);
  if (flatStart === flatNext || flatStart.endsWith(flatNext)) return start;
  if (flatNext.startsWith(flatStart)) return next;
  return joinWithSpace(start, next);
}

/** Punctuate a clause the speaker finished before pausing. */
export function closeSentence(text: string): string {
  const core = tidy(text);
  if (!core || SENTENCE_END.test(core)) return core;
  return `${core.replace(/[,;:]$/, '')}.`;
}

/**
 * Glue the carried-over line to what the engine is reporting now.
 *
 * Engines re-emit an utterance after a restart, so a repeat of the tail is
 * dropped on a punctuation- and case-insensitive comparison. `breakSentence`
 * marks a real think-pause: the previous clause is closed and the next one
 * starts capitalized, which is how native reads a sentence boundary.
 */
export function joinDictation(base: string, tail: string, breakSentence = false): string {
  let left = tidy(base);
  let right = tidy(tail);
  if (!left) return capitalizeFirst(right);
  if (!right) return left;

  const [flatLeft, flatRight] = sameWords(left, right);
  if (flatLeft === flatRight || flatLeft.endsWith(flatRight)) return left;

  if (flatRight.startsWith(flatLeft)) {
    // Same session: this is the hypothesis growing, so take it whole.
    if (!breakSentence) return right;
    // After a pause the engine replayed the committed line before the new
    // words — keep only what is actually new.
    const rest = dropLeadingWords(right, flatLeft.split(' ').length);
    if (!rest) return left;
    right = rest;
  }

  if (breakSentence) {
    left = closeSentence(left);
    right = capitalizeFirst(right);
  } else if (SENTENCE_END.test(left)) {
    right = capitalizeFirst(right);
  }
  return joinWithSpace(left, right);
}

/**
 * Rebuild this engine session from the whole results list.
 *
 * Never append per event: Chrome re-emits already-final results, and an
 * append would stack the same phrase two or three times. Slots are merged
 * rather than concatenated, because successive hypotheses of one utterance
 * may arrive as separate slots.
 */
export function readSession(
  results: ArrayLike<SpeechRecognitionResultLike>,
): { final: string; interim: string } {
  let final = '';
  let interim = '';
  for (let i = 0; i < results.length; i += 1) {
    const piece = tidy(results[i][0]?.transcript || '');
    if (!piece) continue;
    if (results[i].isFinal) final = mergeFragment(final, piece);
    else interim = mergeFragment(interim, piece);
  }
  return { final, interim };
}

function speechLang(): string {
  if (typeof navigator === 'undefined') return 'en-US';
  return navigator.language || 'en-US';
}

interface Options {
  shortId: string;
  value: string;
  onChangeText: (next: string) => void;
  maxChars?: number;
  maxSeconds?: number;
}

export function useWebDictation({
  shortId,
  value,
  onChangeText,
  maxChars = 500,
  maxSeconds = DEFAULT_MAX_SECONDS,
}: Options) {
  const [isRecording, setIsRecording] = useState(false);
  const [converting, setConverting] = useState(false);
  const [mode, setMode] = useState<DictationMode | null>(null);
  const [meteringLevel, setMeteringLevel] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [unavailableReason, setUnavailableReason] = useState<DictationUnavailable | null>(
    canLiveDictate() || canRecordAudio() ? null : DICTATION_UNAVAILABLE.NO_MODULE,
  );

  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChangeText);
  onChangeRef.current = onChangeText;

  const recorderRef = useRef<MediaRecorder | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const meterRafRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const downAtRef = useRef(0);
  const latchedRef = useRef(false);
  const stoppingRef = useRef(false);
  const pendingStopRef = useRef(false);
  const wantLiveRef = useRef(false);
  const prefixRef = useRef('');
  const baseRef = useRef('');
  const sessionFinalRef = useRef('');
  const interimRef = useRef('');
  const lastResultAtRef = useRef(0);
  const pendingBreakRef = useRef(false);
  const tickRef = useRef<number | null>(null);
  const modeRef = useRef<DictationMode | null>(null);
  const startRecordRef = useRef<() => Promise<void>>(async () => {});
  const stopRef = useRef<() => Promise<string>>(async () => '');

  /**
   * Field text = frozen prefix + committed line + this session's words.
   * The committed line only grows, so a mid-sentence revision from the
   * engine can shorten the tail without ever blanking the note.
   */
  const publishLive = useCallback(() => {
    const tail = joinDictation(sessionFinalRef.current, interimRef.current);
    const session = joinDictation(baseRef.current, tail, pendingBreakRef.current);
    const next = joinWithSpace(prefixRef.current, session).slice(0, maxChars);
    if (next === valueRef.current) return next;
    onChangeRef.current(next);
    if (maxChars && next.length >= maxChars) void stopRef.current();
    return next;
  }, [maxChars]);

  /** Fold the finished session into the committed line. */
  const commitSession = useCallback((breakSentence: boolean) => {
    const tail = joinDictation(sessionFinalRef.current, interimRef.current);
    baseRef.current = joinDictation(baseRef.current, tail, pendingBreakRef.current);
    sessionFinalRef.current = '';
    interimRef.current = '';
    pendingBreakRef.current = breakSentence && !!baseRef.current;
  }, []);

  const releaseHardware = useCallback(() => {
    if (meterRafRef.current != null) {
      cancelAnimationFrame(meterRafRef.current);
      meterRafRef.current = null;
    }
    if (tickRef.current != null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (audioCtxRef.current) {
      void audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    recorderRef.current = null;
    setMeteringLevel(0);
    setRemainingSeconds(null);
  }, []);

  const attachSessionClock = useCallback(() => {
    tickRef.current = window.setInterval(() => {
      const elapsed = (Date.now() - startedAtRef.current) / 1000;
      const left = Math.max(0, Math.ceil(maxSeconds - elapsed));
      setRemainingSeconds(left <= COUNTDOWN_VISIBLE_SECONDS ? left : null);
      if (elapsed >= maxSeconds) void stopRef.current();
    }, 250);
  }, [maxSeconds]);

  const startRecord = useCallback(async () => {
    const mimeType = pickRecorderMime();
    if (!mimeType) {
      setUnavailableReason(DICTATION_UNAVAILABLE.NO_MODULE);
      return;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    const recorder = new MediaRecorder(stream, { mimeType });
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorderRef.current = recorder;
    recorder.start(250);
    modeRef.current = 'record';
    setMode('record');
    startedAtRef.current = Date.now();
    setIsRecording(true);
    setUnavailableReason(null);
    if (pendingStopRef.current) {
      pendingStopRef.current = false;
      void stopRef.current();
      return;
    }

    const AudioCtx = window.AudioContext
      || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i += 1) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        setMeteringLevel(Math.min(1, Math.sqrt(sum / data.length) * 4));
        meterRafRef.current = requestAnimationFrame(tick);
      };
      meterRafRef.current = requestAnimationFrame(tick);
    }
    attachSessionClock();
  }, [attachSessionClock]);
  startRecordRef.current = startRecord;

  const bindLive = useCallback((recognition: SpeechRecognitionLike) => {
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = speechLang();
    recognition.onresult = (event) => {
      const { final, interim } = readSession(event.results);
      sessionFinalRef.current = final;
      interimRef.current = interim;
      lastResultAtRef.current = Date.now();
      publishLive();
    };
    recognition.onerror = (event) => {
      const error = event.error || '';
      if (error === 'not-allowed' || error === 'service-not-allowed') {
        wantLiveRef.current = false;
        setUnavailableReason(DICTATION_UNAVAILABLE.NO_PERMISSION);
        void stopRef.current();
        return;
      }
      if (error === 'no-speech' || error === 'aborted') return;
      const heard = baseRef.current || sessionFinalRef.current || interimRef.current;
      if (!heard && canRecordAudio()) {
        wantLiveRef.current = false;
        recognition.abort();
        recognitionRef.current = null;
        void startRecordRef.current();
      }
    };
    recognition.onend = () => {
      if (recognitionRef.current !== recognition) return;
      // Silence ended the session: the speaker finished a sentence.
      const paused = Date.now() - lastResultAtRef.current > SENTENCE_PAUSE_MS;
      commitSession(paused);
      publishLive();
      if (!wantLiveRef.current || stoppingRef.current) return;
      recognitionRef.current = null;
      window.requestAnimationFrame(() => {
        if (!wantLiveRef.current || stoppingRef.current) return;
        const Ctor = speechRecognitionCtor();
        if (!Ctor) return;
        const next = new Ctor();
        bindLive(next);
        try {
          next.start();
          recognitionRef.current = next;
        } catch {
          // Engine refused a new session — keep the line that is already in the field.
        }
      });
    };
  }, [commitSession, publishLive]);

  const startLive = useCallback(() => {
    const Ctor = speechRecognitionCtor();
    if (!Ctor) return false;
    if (recognitionRef.current) return true;
    prefixRef.current = valueRef.current || '';
    baseRef.current = '';
    sessionFinalRef.current = '';
    interimRef.current = '';
    pendingBreakRef.current = false;
    lastResultAtRef.current = Date.now();
    const recognition = new Ctor();
    bindLive(recognition);
    recognition.start();
    recognitionRef.current = recognition;
    wantLiveRef.current = true;
    modeRef.current = 'live';
    setMode('live');
    startedAtRef.current = Date.now();
    setIsRecording(true);
    setUnavailableReason(null);
    attachSessionClock();
    if (pendingStopRef.current) {
      pendingStopRef.current = false;
      void stopRef.current();
    }
    return true;
  }, [attachSessionClock, bindLive]);

  const transcribeBlob = useCallback(async (blob: Blob, mimeType: string) => {
    setConverting(true);
    try {
      const buffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = '';
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
      }
      const result = await transcribeAudioFromWeb({
        shortId,
        contentType: mimeType.split(';')[0],
        base64: btoa(binary),
      });
      if (!result.ok || result.inaudible) {
        setUnavailableReason(DICTATION_UNAVAILABLE.FAILED);
        return valueRef.current;
      }
      const next = appendTranscript(valueRef.current, result.transcript || '').slice(0, maxChars);
      onChangeRef.current(next);
      setUnavailableReason(null);
      return next;
    } catch {
      setUnavailableReason(DICTATION_UNAVAILABLE.FAILED);
      return valueRef.current;
    } finally {
      setConverting(false);
    }
  }, [maxChars, shortId]);

  const stop = useCallback(async () => {
    if (stoppingRef.current) return valueRef.current;
    stoppingRef.current = true;
    latchedRef.current = false;
    pendingStopRef.current = false;
    wantLiveRef.current = false;
    const currentMode = modeRef.current;

    if (currentMode === 'live') {
      const recognition = recognitionRef.current;
      recognitionRef.current = null;
      try {
        recognition?.stop();
      } catch {
        recognition?.abort();
      }
      // No sentence break here — the speaker chose to stop, not to pause.
      commitSession(false);
      const settled = publishLive();
      setIsRecording(false);
      setMode(null);
      modeRef.current = null;
      releaseHardware();
      stoppingRef.current = false;
      return settled;
    }

    const recorder = recorderRef.current;
    if (!recorder) {
      setIsRecording(false);
      setMode(null);
      modeRef.current = null;
      releaseHardware();
      stoppingRef.current = false;
      return valueRef.current;
    }
    if (recorder.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        recorder.addEventListener('stop', () => resolve(), { once: true });
        recorder.stop();
      });
    }
    const mimeType = recorder.mimeType || pickRecorderMime() || 'audio/webm';
    const blob = new Blob(chunksRef.current, { type: mimeType });
    chunksRef.current = [];
    setIsRecording(false);
    setMode(null);
    modeRef.current = null;
    releaseHardware();
    stoppingRef.current = false;
    if (blob.size > 0) return transcribeBlob(blob, mimeType);
    return valueRef.current;
  }, [commitSession, publishLive, releaseHardware, transcribeBlob]);
  stopRef.current = stop;

  const start = useCallback(async () => {
    if (isRecording || converting) return;
    if (!canLiveDictate() && !canRecordAudio()) {
      setUnavailableReason(DICTATION_UNAVAILABLE.NO_MODULE);
      return;
    }
    try {
      if (canLiveDictate()) {
        try {
          startLive();
          return;
        } catch {
          // Fall through to the recorder — iOS Safari often advertises the
          // API and then refuses to start it.
        }
      }
      await startRecord();
    } catch (err) {
      releaseHardware();
      const name = err instanceof DOMException ? err.name : '';
      setUnavailableReason(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? DICTATION_UNAVAILABLE.NO_PERMISSION
          : DICTATION_UNAVAILABLE.FAILED,
      );
    }
  }, [converting, isRecording, releaseHardware, startLive, startRecord]);

  useEffect(() => () => {
    wantLiveRef.current = false;
    try {
      recognitionRef.current?.abort();
    } catch {
      // ignore
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    releaseHardware();
  }, [releaseHardware]);

  const onPointerDown = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    if (converting) return;
    downAtRef.current = Date.now();
    if (isRecording) {
      if (latchedRef.current) void stop();
      return;
    }
    pendingStopRef.current = false;
    void start();
  }, [converting, isRecording, start, stop]);

  const onPointerUp = useCallback(() => {
    const held = Date.now() - downAtRef.current;
    if (held < TAP_THRESHOLD_MS) {
      latchedRef.current = true;
      return;
    }
    latchedRef.current = false;
    if (isRecording) {
      void stop();
      return;
    }
    pendingStopRef.current = true;
  }, [isRecording, stop]);

  const onPointerCancel = useCallback(() => {
    if (latchedRef.current) return;
    if (isRecording) void stop();
  }, [isRecording, stop]);

  const cancel = useCallback(() => {
    wantLiveRef.current = false;
    stoppingRef.current = false;
    latchedRef.current = false;
    chunksRef.current = [];
    try {
      recognitionRef.current?.abort();
    } catch {
      // ignore
    }
    recognitionRef.current = null;
    baseRef.current = '';
    sessionFinalRef.current = '';
    interimRef.current = '';
    pendingBreakRef.current = false;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    if (modeRef.current === 'live') onChangeRef.current(prefixRef.current);
    setIsRecording(false);
    setMode(null);
    modeRef.current = null;
    releaseHardware();
  }, [releaseHardware]);

  return {
    isRecording,
    converting,
    mode,
    meteringLevel,
    remainingSeconds,
    unavailableReason,
    supported: unavailableReason !== DICTATION_UNAVAILABLE.NO_MODULE,
    pressHandlers: {
      onPointerDown,
      onPointerUp,
      onPointerCancel,
    },
    stop,
    cancel,
  };
}
